// Robust tx receipt waiter.
//
// Problem: on flaky public RPCs (BSC testnet especially), `tx.wait()` can
// throw or hang even though the tx was included on-chain. Ethers gives up,
// we show "Failed", user reloads and sees the investment was made anyway.
//
// Fix: try `tx.wait()` first. If it throws OR returns nothing, fall back to
// polling `provider.getTransactionReceipt(hash)` across BOTH providers we have
// available (the signer's provider used to submit, and our own JsonRpcProvider).
// Whichever returns a receipt first wins.

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * @param {Object} tx        ethers TransactionResponse (has .hash)
 * @param {Array}  providers ethers providers to poll — first one that returns
 *                           a receipt wins. Duplicates are OK.
 * @param {Object} [opts]
 * @param {number} [opts.timeoutMs=25000]  hard stop
 * @param {number} [opts.pollMs=1500]      interval between polls
 * @returns {Promise<Object|null>} receipt (may be status:0 = reverted), or null
 *                                 if we couldn't confirm within the timeout.
 */
export const waitForReceiptWithRetry = async (tx, providers, opts = {}) => {
  const timeoutMs = opts.timeoutMs ?? 25000;
  const pollMs    = opts.pollMs    ?? 1500;
  const hash      = tx && tx.hash;
  if (!hash) return null;

  const provList = (Array.isArray(providers) ? providers : [providers]).filter(Boolean);

  // Try the standard wait first — cheapest path on healthy RPC.
  try {
    const r = await tx.wait();
    if (r) return r;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[txWait] tx.wait threw, falling back to polling:', e?.message || e);
  }

  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    // Query every provider in parallel; return the first non-null hit.
    const results = await Promise.all(
      provList.map((p) =>
        p.getTransactionReceipt(hash).catch(() => null)
      )
    );
    for (const r of results) {
      if (r) return r;
    }
    await sleep(pollMs);
  }
  return null;
};

// Normalize ethers status → boolean success. Handles Number, string, undefined.
export const receiptSucceeded = (receipt) => {
  if (!receipt) return false;
  const s = receipt.status;
  if (s === 1 || s === '0x1' || s === '1' || s === true) return true;
  if (s === 0 || s === '0x0' || s === '0' || s === false) return false;
  // Pre-Byzantium chains return null status. If gasUsed > 0 and no reversion
  // marker, assume success — this is a best-effort fallback.
  return s == null;
};
